import { ChangeEvent, useContext, useState } from "react";
import { useForm } from "react-hook-form";
import FileApi from "../api/file";
import AuthContext from "../components/auth-context";
import useAppNavigate from "../components/router/use-app-navigate";
import Form from "../components/inputs/form";
import Select from "../components/inputs/select";
import Textarea from "../components/inputs/textarea";
import Textbox from "../components/inputs/textbox";
import Textfield from "../components/inputs/textfield";
import Center from "../types/center";
import { useTranslation } from "react-i18next";
import VerifyNumber from "../components/signup/verify-number";
import NewRequestResendCodeApi from "../api/newRequest-resendCode";
import { localizeNumbers } from "../functions";
import { Link } from "react-router-dom";

interface RequestForm {
  phone: string;
  subCenterId: string;
  ownershipType: string;
  agentCode: string;
  installAddress: string;
}

function Request() {
  const [city, setCity] = useState("");
  const [center, setCenter] = useState("");
  const [ownershipType, setOwnershipType] = useState("");
  const [phone, setPhone] = useState("");
  const [subCenterId, setSubCenterId] = useState<string>("");
  const [subCenters, setSubCenters] = useState<Center[] | null>(null);
  const [agentCode, setAgentCode] = useState("");
  const [installAddress, setInstallAddress] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [toggle, setToggle] = useState(true);
  const { register, handleSubmit } = useForm<RequestForm>();
  const navigate = useAppNavigate();
  const { t } = useTranslation(undefined, { keyPrefix: "Request.Adsl" });

  const context = useContext(AuthContext);
  const { customerInfo } = context;

  const onSubmit: (data: any) => Promise<string> = async ({
    phone,
    subCenterId,
    ownershipType,
    agentCode,
    installAddress,
  }) => {
    setAgentCode(agentCode);
    setInstallAddress(installAddress);
    setOwnershipType(ownershipType);
    setPhone(phone);
    setSubCenterId(subCenterId);

    return new NewRequestResendCodeApi(context).get("ADSL").then((response) => {
      if (!response) setIsVerifying(true);
      return response;
    });
  };

  const onVerifyNumberSubmit = async () => {
    new FileApi(context)
      .post(0, phone, subCenterId, agentCode, ownershipType, installAddress, "")
      .then((response) => {
        if (response && response.id) {
          context.setActiveFileId(response.id);
          navigate("/");
        } else {
          return response.errorMessage || "خطایی رخ داده است.";
        }
        return "";
      });
  };

  const handlePhoneChange = ({
    target: { value },
  }: ChangeEvent<HTMLInputElement>) => {
    console.log("handlePhoneChange", value);
    if (parseInt(value).toString().length === 10) {
      console.log("handlePhoneChange", value, "here");
      fetch(`/api/Inquiry/${value}/`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      })
        .then((response) => response.json())
        .then((data) => {
          console.log("data", data);
          setPhone(value);
          setCity(data.City);
          setCenter(data.Center);
          setSubCenters(
            data.SubCentersList.map((sc: { Title: string; Id: number }) => ({
              title: sc.Title,
              id: sc.Id,
            }))
          );
        })
        .catch(console.error);
    }
  };

  return (
    <>
      {!isVerifying ? (
        <Form submit={onSubmit} handleSubmit={handleSubmit}>
          <h3 className="lead-title pt-2">{t("Title")}</h3>
          <Textbox
            {...register("phone", { required: true })}
            label="تلفن"
            helpNote="شماره تلفن بصورت کامل به همراه کد شهر. نمونه: ۰۲۱۸۴۲۱۱۰۰۰"
            onChange={handlePhoneChange}
          />
          {subCenters ? (
            <Select
              {...register("subCenterId", { required: true })}
              label="مجتمع"
              helpNote="در صورت سکونت در هر یک از مجتمع های موجود در لیست، لطفا مجتمع محل سکونت خود را انتخاب نمایید."
              options={[
                { name: "", value: "لطفا یکی از گزینه‌ها را انتخاب نمایید" },
                { name: "0", value: "هیچ کدام" },
                ...subCenters.map((subCenter) => ({
                  name: subCenter.id.toString(),
                  value: subCenter.title,
                })),
              ]}
              onChange={(e) => setSubCenterId(e.target.value)}
            />
          ) : (
            <input {...register("subCenterId")} type="hidden" value="0" />
          )}
          <Textfield label="شهر" value={city} />
          <Textfield label="مرکز مخابراتی" value={center} />
          <Select
            {...register("ownershipType")}
            label="نوع مالکیت"
            name="ownershipType"
            options={[
              { name: "Owner", value: "مالک" },
              { name: "Renter", value: "مستاجر" },
            ]}
            onChange={(e) => setOwnershipType(e.target.value)}
          />
          <Textbox
            {...register("agentCode")}
            label="کد نمایندگی"
            helpNote="در صورتی که توسط یک نماینده جهت ثبت نام معرفی شده اید."
            onChange={(e) => setAgentCode(e.target.value)}
          />
          <Textarea
            {...register("installAddress")}
            label="آدرس نصب"
            helpNote="در صورت نیاز به نصب مودم توسط کارشناس شرکت، تعیین آدرس مراجعه مورد نیاز است."
            onChange={(e) => setInstallAddress(e.target.value)}
          />
          <div className="form-section rtl flex flex-grow xs:flex-col sm:flex-row  gap-4 justify-between">
            <div className="rule-section flex algin-center items-center gap-2">
              <input
                id="role-checkbox"
                type="checkbox"
                tabIndex={19}
                onClick={() => setToggle(!toggle)}
              />
              <label className="role-checkbox-label" htmlFor="role-checkbox">
                <p className="w-50">
                  {t("I had read and accept the contract", {
                    name: `${customerInfo?.firstName ?? ""} ${
                      customerInfo?.lastName ?? ""
                    }`,
                    nationalCode: localizeNumbers(customerInfo?.nationalCode!),
                    service: "Td-Lte",
                    username: localizeNumbers(customerInfo?.cellPhone!),
                  })}
                  <small>
                    <Link to="/signin" className="btn-show-role font-bold">
                      ({t("Read contract")})
                    </Link>
                  </small>
                </p>
              </label>
            </div>
          </div>
          <div className="rule-section flex algin-center items-center gap-2">
            <button
              disabled={toggle}
              className="signup-btn p-5 flex"
              tabIndex={20}
            >
              {t("Accept & Continue")}
              <span className="sepanta-icon-left-long-arrow"></span>
            </button>
          </div>
        </Form>
      ) : (
        <VerifyNumber
          username={customerInfo?.cellPhone!}
          onSubmit={onVerifyNumberSubmit}
          handleAlert={(msg) => alert(msg)}
          alert={false}
          serviceType={"ADSL"}
        />
      )}
    </>
  );
}

export default Request;
